import { useCallback, useEffect, useRef } from 'react'

type FieldElement = HTMLInputElement | HTMLSelectElement | HTMLButtonElement

function purgeStale(set: Set<FieldElement>) {
  set.forEach((el) => {
    if (!document.contains(el)) {
      set.delete(el)
    }
  })
}

export default function useTabGroup() {
  const tabElements = useRef<Set<FieldElement>>(new Set())
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  function registerTabElement(ref: FieldElement | null): void {
    if (ref) {
      tabElements.current.add(ref)
    } else {
      purgeStale(tabElements.current)
    }
  }

  const focusNextElement = useCallback((triggerClick = false, fromElement?: FieldElement) => {
    if (!document) return

    const focusOrClick = triggerClick ? 'click' : 'focus'

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    if (fromElement) {
      timeoutRef.current = setTimeout(() => {
        // Re-snapshot live elements inside the timeout so we never target
        // a DOM node that was destroyed by a React key-change remount
        purgeStale(tabElements.current)
        const liveList = Array.from(tabElements.current)
        const idx = liveList.indexOf(fromElement)

        if (idx === -1 || idx + 1 >= liveList.length) return

        // If focus already advanced past fromElement (e.g. Tab key),
        // don't override — the browser already handled it
        const currentFocus = document.activeElement as FieldElement
        const currentIdx = liveList.indexOf(currentFocus)
        if (currentIdx > idx) return

        liveList[idx + 1][focusOrClick]()
      }, 100)
      return
    }

    const elementList = Array.from(tabElements.current)
    if (elementList.length === 0) return

    const { activeElement } = document
    if (
      activeElement &&
      (activeElement instanceof HTMLInputElement ||
        activeElement instanceof HTMLSelectElement ||
        activeElement instanceof HTMLButtonElement)
    ) {
      const selectedItemIndex = elementList.indexOf(activeElement)

      if (selectedItemIndex === -1 || selectedItemIndex + 1 >= elementList.length) {
        timeoutRef.current = setTimeout(() => elementList[0][focusOrClick](), 0)
        return
      }
      timeoutRef.current = setTimeout(() => elementList[selectedItemIndex + 1][focusOrClick](), 0)
    }
  }, [])

  return {
    registerTabElement,
    focusNextElement,
  }
}
